export type PartialWithRequiredFields<T, K extends keyof T = keyof T> = Partial<T> &
  Pick<Required<T>, K>

export type StringKeys<T> = {
  [K in keyof T]: T[K] extends string ? K : never
}[keyof T]

export type Optional<T> = T | undefined | null

export type NonNull<T> = Exclude<T, null>
export type NonNullOptional<T> = NonNull<Optional<T>>

export interface SelectModelEntry<T> {
  name: string
  code: T
}
export type SelectModel<T> = Array<SelectModelEntry<T>>
export type SelectModelWithNull<T> = Array<SelectModelEntry<T> | { name: '-'; code: null }>

export const createSelectModel = <T>(entries: SelectModelEntry<T>[]): SelectModelWithNull<T> => [
  { name: '-', code: null },
  ...entries
]

export const withEmptyElement = <T>(model: SelectModel<T>): SelectModelWithNull<T> => {
  return createSelectModel<T>(model)
}

export const isNull = <T>(entry: SelectModelEntry<T>): boolean => entry.code === null
export const hasNoValue = <T>(entry?: SelectModelEntry<T>): boolean =>
  !entry ? true : isNull(entry)

export interface PopoverSelectModelEntry<T> extends SelectModelEntry<T> {
  text: string
}
export type PopoverSelectModelEntryWithNull<T> =
  | PopoverSelectModelEntry<T>
  | typeof emptyPopoverSelectModelEntry

export type PopoverSelectModel<T> = Array<PopoverSelectModelEntry<T>>
export type PopoverSelectModelWithNull<T> = Array<PopoverSelectModelEntryWithNull<T>>

export const createPopoverSelectModel = <T>(
  entries: PopoverSelectModelEntry<T>[]
): PopoverSelectModelWithNull<T> => [
  emptyPopoverSelectModelEntry, // Default empty text for null entry
  ...entries
]

export const withEmptyPopoverElement = <T>(
  model: PopoverSelectModel<T>
): PopoverSelectModelWithNull<T> => {
  return createPopoverSelectModel<T>(model)
}

export const emptyPopoverSelectModelEntry = { name: '-', code: null, text: '' } as const
